import { requireAuth } from '@/auth/requireAuth';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { PageTitle } from '@/layout/page-title';
import { cn } from '@/lib/utils';
import { deleteRole, getPeople, getPerson, getRolesList, patchPerson, type PersonaRuoloCompanyDTO } from '@/people';
import { createFileRoute, Link, useLoaderData, useRouter } from '@tanstack/react-router'
import { ArrowLeft, CheckIcon, ChevronsUpDownIcon, Loader2, Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from "@hookform/resolvers/zod"
import toast from 'react-hot-toast';
import { LoadingSpinner } from '@/components/ui/spinner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AddRole } from '@/components/users/add-role';
import type { Company } from '@/company';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';

export const Route = createFileRoute('/(app)/_layout/users/$email')({
  component: RouteComponent,
  beforeLoad: requireAuth,
  loader: async (opts) => {
    const email = opts.params.email as string;
    const { data: people, error: _ } = await getPeople();
    const { data, error } = await getPerson(email);
    const { data: roles, error: __ } = await getRolesList();
    return { user: data, error, people, roles };
  }
})

function RouteComponent() {

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingDeleteRole, setLoadingDeleteRole] = useState(false);
  const { user, error, people, roles } = useLoaderData({ from: '/(app)/_layout/users/$email' });
  const { companiesData } = useLoaderData({ from: '/(app)/_layout' });
  const peopleEmails = people?.map(person => person.email) || [];

  if (error) {
    return <div>Error loading user details</div>;
  }

  const formSchema = z.object({
    email: z.string().min(2, {
      message: "Email must be at least 2 characters.",
    }),
    name: z.string().min(2, {
      message: "Name must be at least 2 characters.",
    }),
    managerEmail: z.string().optional(),
    isAdmin: z.boolean(),
    isClevel: z.boolean(),
  })
  const router = useRouter();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
      managerEmail: user?.managerEmail || '',
      isAdmin: user?.isAdmin || false,
      isClevel: user?.isClevel || false,
    },
    mode: 'onChange',

  })

  async function onSubmit(values: z.infer<typeof formSchema>) {
    // Do something with the form values.
    // ✅ This will be type-safe and validated.
    setLoading(true)
    const data = {
      name: values.name,
      email: values.email,
      managerEmail: values.managerEmail,
      isAdmin: values.isAdmin,
      isClevel: values.isClevel,
    };
    const { error } = await patchPerson(values.email, data);
    setLoading(false)
    if (error) {
      toast.error("Error updating user: " + error);
      return;
    }
    toast.success("User updated successfully!");
    router.invalidate();
  }

  const handleDeleteRole = async (companyId: string, role: string) => {
    // Implement role deletion logic here
    setLoadingDeleteRole(true)
    const { error } = await deleteRole({
      companyId, role, personEmail: user!.email
    });
    setLoadingDeleteRole(false)

    if (error) {
      toast.error("Error deleting role: " + error);
      return;
    }
    toast.success("Role deleted successfully!");
    router.invalidate();
  }

  // Group user roles by companyId
  const groupedRoles = (user?.roles ?? []).reduce<Record<string, PersonaRuoloCompanyDTO[]>>((acc, role) => {
    if (!role.companyId) return acc;
    if (!acc[role.companyId]) acc[role.companyId] = [];
    acc[role.companyId].push(role);
    return acc;
  }, {});


  return (
    <div className="container max-w-[1900px] mx-auto py-10 px-4">
      <div className="space-y-4">
        <PageTitle
          leftActions={
            <Button asChild variant={'ghost'}>
              <Link to="/users" className="text-sm">
                <ArrowLeft className="mr-2" />
                Back to Users
              </Link>
            </Button>
          }
          title={`User Details for ${user?.email}`} />

        {/* You can add more user details here */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card >
            <CardHeader>
              <CardTitle >User information</CardTitle>
            </CardHeader>
            <CardContent>

              <Form {...form} >
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">

                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input placeholder="name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input disabled placeholder="email@example.com" {...field} />
                        </FormControl>
                        <FormDescription>This is your public display email.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="managerEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Manager Email</FormLabel>
                        <FormControl>
                          <Popover open={open} onOpenChange={setOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={open}
                                className="w-full h-8 justify-between overflow-hidden"
                              >
                                {field.value ? field.value
                                  : "Select manager..."}
                                <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[300px] p-0">
                              <Command>
                                <CommandInput placeholder="Search company..." />
                                <CommandList>
                                  <CommandEmpty>No email found.</CommandEmpty>
                                  <CommandGroup>
                                    {peopleEmails.map((email) => (
                                      <CommandItem
                                        key={email}
                                        value={email}
                                        onSelect={() => {
                                          field.onChange(email)
                                          setOpen(false)  // Close the popover after selection
                                        }
                                        }

                                      >
                                        <CheckIcon
                                          className={cn(
                                            "mr-2 h-4 w-4",
                                            field.value === email ? "opacity-100" : "opacity-0"
                                          )}
                                        />
                                        {email}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </FormControl>
                        <FormDescription>Select the manager's email from the list.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="isAdmin"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Is Admin</FormLabel>
                        <FormControl>
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={e => field.onChange(e.target.checked)}
                          />
                        </FormControl>
                        <FormDescription>Admin privileges for this user.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="isClevel"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Is C-Level</FormLabel>
                        <FormControl>
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={e => field.onChange(e.target.checked)}
                          />
                        </FormControl>
                        <FormDescription>enable C-Level capabilities</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    disabled={loading}
                    type="submit"
                    className="mt-4"
                  >
                    Save Changes
                    {loading && <LoadingSpinner />}
                  </Button>
                </form>
              </Form>
            </CardContent>

          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Roles</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4">
                <div className="flex-1/6">

                  <AddRole
                    personEmail={user!.email}
                    companies={companiesData.data ?? []}
                    roles={roles ?? []}
                  />
                </div>
                <div className="overflow-y-auto flex-5/6 max-h-[600px]">

                  {
                    (user?.roles?.length ?? 0) === 0 && <div>

                      No roles assigned to this user.
                    </div>
                  }
                  {
                    Object.entries(groupedRoles).map(([companyId, roles]) => {
                      const company = companiesData.data?.find((c: Company) => c.id === companyId)
                      const displayName = company ? `${company.sapCode}-${company.legalEntity}` : "Unknown Company";
                      return (
                        <div key={companyId}>
                          <p className="font-semibold">{displayName}</p>
                          <Table>
                            <TableBody>


                              {roles && roles.length > 0 && roles.map(role => (
                                <TableRow key={role.role + role.companyId}>
                                  <TableCell>

                                    <p key={role.role}>{role.role}</p>
                                  </TableCell>
                                  <TableCell>

                                    <Button disabled={loadingDeleteRole} size="sm" variant="destructive" onClick={() =>
                                      handleDeleteRole(companyId, role.role)
                                    }>
                                      {!loadingDeleteRole && <Trash2Icon className="h-4 w-4" />}
                                      {loadingDeleteRole && <Loader2 className="animate-spin" />}
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      );
                    })
                  }
                </div>

              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
